import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useCreateTopic } from '../lib/api'
import { CODE_AGENTS, chatTarget, type CodeAgent } from '../lib/chatNavigation'
import AgentBadge from './AgentBadge'

const [CLAUDE_CODE, CODEX_CODE] = CODE_AGENTS

/** Shared session shortcuts, anchored to the chat-list pane on both tabs. */
export default function CodeConversationButtons({ parentTab = 'code' }: { parentTab?: 'code' | 'lanes' }) {
  const navigate = useNavigate()
  const createClaude = useCreateTopic(CLAUDE_CODE.chatId)
  const createCodex = useCreateTopic(CODEX_CODE.chatId)
  const [startingAgent, setStartingAgent] = useState<CodeAgent | null>(null)
  const starting = useRef(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Compose creates the conversation EMPTY and navigates into the real chat
  // window. That is the whole point: Model and Thinking live in the Composer,
  // so they are only reachable once a topic exists — collecting the opening
  // message in a modal first meant Mark had to send before he could choose.
  const startConversation = useCallback(async (agent: CodeAgent) => {
    if (starting.current) return
    starting.current = true
    const meta = agent === 'codex' ? CODEX_CODE : CLAUDE_CODE
    const create = agent === 'codex' ? createCodex : createClaude
    setActionError(null)
    setStartingAgent(agent)
    try {
      const created = await create.mutateAsync({ opening_prompt: '' })
      navigate(`${chatTarget(meta.chatId, created.id, { parentTab })}&focus_composer=1`)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not start the conversation')
    } finally {
      starting.current = false
      setStartingAgent(null)
    }
  }, [createClaude, createCodex, navigate, parentTab])

  return (
    <>
      {actionError && (
        <div role="alert" className="absolute bottom-40 left-3 right-3 z-30 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {actionError}
        </div>
      )}
      <div className="absolute bottom-4 right-4 z-30 flex flex-col items-end gap-3">
        {[...CODE_AGENTS].reverse().map((meta) => (
          <button
            key={meta.chatId}
            type="button"
            onClick={() => void startConversation(meta.agent)}
            disabled={startingAgent !== null}
            aria-label={`New ${meta.label} conversation`}
            title={`New ${meta.label} conversation`}
            className={`grid h-14 w-14 place-items-center rounded-full text-white shadow-lg transition-colors focus-visible:outline-none focus-visible:ring-2 ${
              meta.agent === 'claude'
                ? 'bg-[#D97757] hover:bg-[#c76a4b] focus-visible:ring-[#edb39e]'
                : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-400'
            }`}
          >
            <span className="relative grid place-items-center">
              <AgentBadge agent={meta.agent} size={26} />
              <span
                className={`absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-white shadow ring-1 ${
                  meta.agent === 'claude'
                    ? 'text-[#b85c3e] ring-[#f0c9b8]'
                    : 'text-blue-700 ring-blue-200'
                }`}
              >
                <Plus className="h-3 w-3" aria-hidden />
              </span>
            </span>
          </button>
        ))}
      </div>
    </>
  )
}
